/* eslint-disable @next/next/no-img-element */

import Link from 'next/link';
import classNames from 'classnames';
import { fromUnixTime } from 'date-fns';
import { MatchListItemFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import { isMatchDecided } from '@amf/shared/utils/matchState';
import { useTranslate } from '@tolgee/react';
import { ReactNode } from 'react';
import FormattedDate from '@amf/shared/components/utils/FormattedDate';

interface MatchListItemTeamProps {
  team?: TeamListItemFragment;
  type: 'home' | 'visiting';
}

function MatchListItemTeam({ type, team }: MatchListItemTeamProps) {
  if (!team) {
    return (
      <div
        className={classNames(
          'MatchListItem__team',
          'MatchListItem__team--empty',
          `MatchListItem__team--${type}`,
        )}
      >
        <div className='MatchListItem__teamBrand' />
        <h5 className='MatchListItem__teamName'>-</h5>
      </div>
    );
  }

  return (
    <div className={classNames('MatchListItem__team', `MatchListItem__team--${type}`)}>
      {team.club.brandUrl ? (
        <img className='MatchListItem__teamBrand' src={team.club.brandUrl} alt={team.club.name} />
      ) : (
        <div className='MatchListItem__teamBrand' />
      )}
      <h5 className='MatchListItem__teamName'>{team.name}</h5>
    </div>
  );
}

interface MatchListItemTimeProps {
  date: Date;
}

function MatchListItemTime({ date }: MatchListItemTimeProps) {
  return (
    <div className='MatchListItem__time'>
      <span>
        <FormattedDate date={date} format='EEEEEE HH:mm' />
      </span>
      <small>
        <FormattedDate date={date} format='dd/MM/yy' />
      </small>
    </div>
  );
}

interface MatchListItemScoreProps {
  match: MatchListItemFragment;
  date: Date;
  live: boolean;
}

function MatchListItemScore({ match, date, live }: MatchListItemScoreProps) {
  const { t } = useTranslate();

  return (
    <div className='MatchListItem__score'>
      <span>{match.score}</span>
      {live ? (
        <small>{t('match.list.now')}</small>
      ) : match.contumacy ? (
        <small>{t('match.list.contumacy')}</small>
      ) : (
        <small>
          <FormattedDate date={date} format='dd/MM/yy HH:mm' />
        </small>
      )}
    </div>
  );
}

interface MatchListItemProps {
  match: MatchListItemFragment;
  prefix: string;
  round?: ReactNode;
  info?: ReactNode;
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function MatchListItem({ match, prefix, round, info }: MatchListItemProps) {
  const { t } = useTranslate();
  const start = fromUnixTime(match.dateStart);
  const end = match.dateEnd ? fromUnixTime(match.dateEnd) : null;
  const live = end === null && match.live && match.started;
  const future = !isMatchDecided(match);

  return (
    <Link href={`${prefix}/${match.id}`}>
      <a
        className={classNames('MatchListItem', {
          'MatchListItem--live': live,
        })}
      >
        <div className='MatchListItem__main'>
          {round && <div className='MatchListItem__round'>{round}</div>}
          {match.ground && (
            <div className='MatchListItem__ground'>
              <i className='MatchListItem__groundIcon icons-stadium' />
              <div className='MatchListItem__groundContent' title={t('match.list.ground')}>
                <span>{match.ground?.name ?? t('match.list.ground.empty')}</span>
              </div>
            </div>
          )}
        </div>
        <MatchListItemTeam team={match.home?.team} type='home' />
        {future && !live && <MatchListItemTime date={start} />}
        {(!future || live) && <MatchListItemScore match={match} date={start} live={live} />}
        <MatchListItemTeam team={match.visiting?.team} type='visiting' />
        <div className='MatchListItem__info'>
          {match.liveStreamEnabled && (
            <span className='MatchListItem__infoItem MatchListItem__infoItem--red'>
              <i className='bi bi-tv-fill' />
              {t('match.list.liveStream')}
            </span>
          )}
          {match.news.length > 0 && (
            <span className='MatchListItem__infoItem'>
              <i className='bi bi-newspaper' />
              {t('match.list.news')}
            </span>
          )}
          {match.subtitle && <span className='MatchListItem__infoItem'>{match.subtitle}</span>}
          {info}
        </div>
        <i className='MatchListItem__chevron bi bi-chevron-right' />
      </a>
    </Link>
  );
}

export default MatchListItem;
